<template>
  <article>
    <h1 class="text-h3" id="intro" v-intersect="onIntersect">Datasets</h1>
    <p>
      A dataset is a compilation of two files for the purpose of generating predictions. Filters can be applied to
      datasets to limit their size and scope.
    </p>

    <p>
      Datasets exist to help organize and filter files for use in predictions or applications that require
      data organized in a particular fashion.
    </p>
    <v-card outlined color="grey lighten-3">
      <v-card-title>Dataset</v-card-title>
      <v-card-subtitle>
        A dataset consisting of well completion and production details
      </v-card-subtitle>
      <v-data-table
        tile
        dense
        :items="dataset.items"
        :headers="dataset.headers"
        hide-default-footer
        disable-sort
      ></v-data-table>
    </v-card>

    <v-alert type="info" border="left" text>
      Datasets are not used anywhere else, except when generating predictions.
    </v-alert>

    <h2 class="text-h4" id="datasetCreate" v-intersect="onIntersect">Compiling datasets</h2>
    <p>
      Compiling a dataset means linking two files together and optionally applying some filters. A dataset consists of
      <strong>COMPLETION</strong>
      and
      <strong>PRODUCTION</strong>
      data, both of which are necessary for use in generating predictions. Each file maintains its own existence, and
      can still be modifed on its own.
    </p>
    <p>
      Dataset complilation is a 2-stage process &mdash; an initial setup phase, followed by optional filter
      specification.
    </p>

    <h3 class="text-h5" id="basic" v-intersect="onIntersect">Initial setup</h3>
    <ol>
      <li>Provide a name for the dataset</li>
      <li>Identify completion and production files</li>
      <li>Select the unique well identifier</li>
    </ol>
    <v-card outlined><v-img contain eager src="@/assets/images/dataset_compile.png"></v-img></v-card>

    <h3 class="text-h5" id="filters" v-intersect="onIntersect">Applying filters</h3>
    <p>
      Completion data may contain thousands of rows of data. To restrict a dataset to only the data of interest, you
      create filters. Filters are actually just different conditions applied to a dataset to limit its scope.
    </p>
    <p>Multiple conditions can be combined using operators and groups to create complex filters.</p>
    <ul>
      <li>
        Click
        <v-icon>mdi-plus</v-icon>
        and
        <v-icon>mdi-minus</v-icon>
        to add/remove filters &mdash; select a filter before removing it; selected filters are highlighted in
        <code class="blue white--text">blue</code>
        .
      </li>
      <li>
        Operators include the keywords
        <code>AND</code>
        and
        <code>OR</code>
      </li>
      <li>
        Conditions can be grouped using brackets
        <code>(</code>
        and
        <code>)</code>
      </li>
      <li>
        <v-icon color="green">mdi-check-circle-outline</v-icon>
        will indicate a valid filter
      </li>
      <li>
        <v-icon color="red">mdi-alert-circle-outline</v-icon>
        will indicate an invalid filter
      </li>
    </ul>
    <v-card outlined><v-img contain eager src="@/assets/images/dataset_filters.png"></v-img></v-card>

    <p>
      To create a new filter, click
      <v-icon>mdi-plus</v-icon>
      to open the filter creation dialog.
    </p>
    <v-card outlined><v-img contain eager src="@/assets/images/dataset_filter_dialog.png"></v-img></v-card>
    <ol>
      <li>
        Select a file &mdash;
        <code>completion</code>
        or
        <code>production</code>
      </li>
      <li>Select a column &mdash; from a list of columns only found in the seleted file</li>
      <li>
        Select an operator &mdash; options include
        <code title="sdcsdcs">in</code>
        ,
        <code>equals</code>
        ,
        <code>contains</code>
        ,
        <code>is less than</code>
        ,
        <code>is greater than</code>
        <ul>
          <li>
            Check
            <code>not</code>
            to invert the selected operation
          </li>
        </ul>
      </li>
      <li>
        Select a value &mdash; depending on the choice of column, the control will either allow you to select from a
        list of values, or type in a numeric value
      </li>
      <li>
        Click
        <code class="green white--text">CREATE</code>
        to apply filter
      </li>
    </ol>
    <p>
      Repeat this process to add more filters. Use the options in the toolbar to create operators and groups.
    </p>
    <p>
      When you are satisfied with your filters, click
      <code class="green white--text">COMPILE</code>
      to create a new dataset
    </p>

    <v-alert type="info" border="left" text>
      Number of rows in a dataset, as a result of the filter, can only be seen when you create or update a dataset.
    </v-alert>

    <h2 class="text-h4" id="datasetShow" v-intersect="onIntersect">Viewing datasets</h2>
    <p>
      A complete list of available datasets is viewable from the
      <code>DATASETS</code>
      menu. The list shows the
      <strong>NAME</strong>
      of the dataset, and when it was last
      <strong>UPDATED</strong>
      . When there is space for more information, name of the original files are also visible.
    </p>
    <p>
      Clicking on an item will load that dataset in the
      <code>VIEW DATASET</code>
      page. The dataset view mode provides an in-depth look at the available data, and their properties through three
      different tabs &mdash;
      <strong>INFO</strong>
      ,
      <strong>DATA</strong>
      , and
      <strong>DETAILS</strong>
      .
    </p>

    <h3 class="text-h5" id="data" v-intersect="onIntersect">Data</h3>
    <p>
      Shows the actual contents of the file &mdash; however many rows that fit the screen, normally called the HEAD.
      This helps identify the file from its contents.
    </p>
    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/file_view_data.png"></v-img>
    </v-card>
    <p>
      The table used to show the data, does not wrap the contents, and scrolls to the right if there are more columns
      than can fit on the screen.
    </p>

    <h3 class="text-h5" id="info" v-intersect="onIntersect">Information</h3>
    <p>
      Shows basic statistics of each of the data columns in the dataset. Each statistic provides some insight into the
      nature of the uploaded data and can help identify potential problems.
    </p>
    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/file_view_info.png"></v-img>
    </v-card>

    <h3 class="text-h5" id="details" v-intersect="onIntersect">Details</h3>
    <p>
      Outlines object-level properties, including name of the source files, who owns this dataset, and when it was
      created and/or last updated. You can click the name of the files to view the original contents.
    </p>
    <v-card flat outlined color="grey">
      <v-img contain eager src="@/assets/images/dataset_view_details.png"></v-img>
    </v-card>

    <h2 class="text-h4" id="datasetEdit" v-intersect="onIntersect">Editing datasets</h2>
    <p>
      Datasets can be edited; i.e. users can change the name or the composition of a dataset. Filters can also be
      removed or modified. To edit a dataset, click
      <v-icon>mdi-pencil-outline</v-icon>
      when viewing a dataset..
    </p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/dataset_edit.png"></v-img></v-card>
    <p>
      See the section on
      <router-link :to="{ path: '/help/datasets', hash: '#filters' }">filters</router-link>
      to view how to manage filters.
    </p>
    <v-alert type="info" text border="left">
      Dataset will be reconstructed from composite files when updated.
      <br />
      The process runs in the background, and large datasets may take a while.
    </v-alert>
    <v-alert type="info" border="left" text>
      Datasets are not automatically updated when files are updated. Click
      <v-icon light>mdi-refresh</v-icon>
      to update a dataset.
    </v-alert>

    <h2 class="text-h5" id="datasetDelete" v-intersect="onIntersect">Deleting datasets</h2>
    <p>
      Datasets can be deleted from the
      <code>DELETE</code>
      page. The application asks for confirmation before deleting a dataset.
    </p>
    <v-card flat outlined color="grey"><v-img contain eager src="@/assets/images/dataset_delete.png"></v-img></v-card>
    <v-alert outlined text type="error">Deleting a dataset will remove all related predictions.</v-alert>

    <contents :items="menu" ref="contents" />
  </article>
</template>
<script>
import Contents from "@/components/contents"
export default {
  name: "HelpData",
  components: { Contents },
  data() {
    return {
      menu: [
        { id: "intro", text: "Introduction", depth: 1 },
        { id: "datasetCreate", text: "Compiling", depth: 1 },
        { id: "basic", text: "Initial setup", depth: 2 },
        { id: "filters", text: "Applying filters", depth: 2 },
        { id: "datasetList", text: "Browsing", depth: 1 },
        { id: "datasetShow", text: "Viewing", depth: 1 },
        { id: "data", text: "Data", depth: 2 },
        { id: "info", text: "Information", depth: 2 },
        { id: "details", text: "Details", depth: 2 },
        { id: "datasetEdit", text: "Editing", depth: 1 },
        { id: "datasetDelete", text: "Deleting", depth: 1 }
      ],
      dataset: {
        headers: [
          { text: "Well API", value: "api" },
          // { text: "County", value: "county" },
          { text: "Surface Latitude", value: "latitude" },
          { text: "Surface Longitude", value: "longitude" },
          { text: "Lateral Length", value: "length" },
          // { text: "Completion Date", value: "date" },
          { text: "Proppant Amount", value: "proppant" },
          { text: "Production Date", value: "date" },
          { text: "Oil Volume", value: "oil" },
          { text: "Gas Volume", value: "gas" },
          { text: "Water Volume", value: "water" }
        ],
        items: [
          {
            api: "1000000000",
            county: "County A",
            latitude: "30.000000",
            longitude: "101.000000",
            length: 4500,
            date: "2010-10-01",
            proppant: 300,
            date: "2010-06-01",
            oil: 500,
            gas: 300,
            water: 5000
          },
          {
            api: "1000000001",
            county: "County B",
            latitude: "30.500000",
            longitude: "101.500000",
            length: 4600,
            date: "2010-11-01",
            proppant: 250,
            date: "2010-07-01",
            oil: 1500,
            gas: 400,
            water: 4000
          },
          {
            api: "1000000002",
            county: "County C",
            latitude: "32.000000",
            longitude: "102.000000",
            length: 5000,
            date: "2010-12-01",
            proppant: 150,
            date: "2010-08-01",
            oil: 10000,
            gas: 500,
            water: 3000
          },
          {
            api: "1000000003",
            county: "County A",
            latitude: "32.000000",
            longitude: "102.000000",
            length: 5432,
            date: "2010-10-01",
            proppant: 350,
            api: "1000000000",
            date: "2010-09-01",
            oil: 15000,
            gas: 250,
            water: 2000
          },
          {
            api: "1000000004",
            county: "County B",
            latitude: "32.550000",
            longitude: "102.550000",
            length: 3500,
            date: "2010-11-01",
            proppant: 450,
            date: "2010-10-01",
            oil: 12000,
            gas: 100,
            water: 1000
          }
        ]
      }
    }
  }
}
</script>